import { Icon } from '@gftg/ui';
import { Link } from 'react-router';
import { sessionPath } from '../lib/paths';
import { initialsFromName } from '../lib/people';
import type { Session } from '../lib/queries';
import { useSavedSessions } from '../lib/savedSessions';
import { SessionSummary } from './SessionSummary';

interface SessionCardProps {
  session: Session;
  timeZone: string;
}

/**
 * The agenda row. The whole card is the link and the star sits above it, rather than the star
 * being nested inside an anchor — a button inside a link is not something a browser or a screen
 * reader can make sense of.
 */
export function SessionCard({ session, timeZone }: SessionCardProps) {
  const saved = useSavedSessions();
  const isSaved = saved.isSaved(session.id);
  const note = sessionNote(session);

  return (
    <article className="rounded-card relative border border-border bg-surface p-4">
      <Link
        to={sessionPath(session)}
        className="rounded-card absolute inset-0"
        aria-label={session.title}
      />

      <SessionSummary session={session} timeZone={timeZone} />

      <div className="mt-4 flex items-center gap-3">
        <div className="pointer-events-none min-w-0 flex-1">
          {session.speakers.length > 0 ? (
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex shrink-0 gap-1">
                {session.speakers.slice(0, 3).map((speaker) => (
                  <span
                    key={speaker.id}
                    className="flex size-6 items-center justify-center rounded-full bg-sand-3 text-[0.6rem] font-bold text-ink-2"
                    aria-hidden="true"
                  >
                    {initialsFromName(speaker.name)}
                  </span>
                ))}
              </div>
              <p className="text-subtitle truncate text-ink-2">
                {session.speakers.map((speaker) => speaker.name).join(' · ')}
              </p>
            </div>
          ) : (
            note && (
              <p
                className={`text-subtitle ${session.isCancelled ? 'text-alert-text' : 'text-ink-3'}`}
              >
                {note}
              </p>
            )
          )}
        </div>

        {/* SCH-01, on this device only until `starred_sessions` has a write endpoint. */}
        <button
          type="button"
          onClick={() => saved.toggle(session.id)}
          aria-pressed={isSaved}
          aria-label={
            isSaved ? `Remove ${session.title} from my schedule` : `Save ${session.title}`
          }
          className={`relative flex size-tap shrink-0 items-center justify-center rounded-full ${
            isSaved ? 'bg-ink text-white' : 'bg-sand-2 text-ink-4'
          }`}
        >
          <Icon name="star" size={19} filled={isSaved} strokeWidth={isSaved ? 0 : 1.5} />
        </button>
      </div>

      {session.speakers.length > 0 && note && (
        <p
          className={`text-subtitle pointer-events-none mt-2 ${
            session.isCancelled ? 'text-alert-text' : 'text-ink-3'
          }`}
        >
          {note}
        </p>
      )}
    </article>
  );
}

/** One line under the card, in the order that matters if several apply at once. */
function sessionNote(session: Session): string | null {
  if (session.isCancelled) {
    return session.cancellationReason ?? 'Cancelled';
  }
  return session.eligibilityNote;
}
